import React, { useCallback, useEffect, useState } from "react";
import { CompaniesList, ProjectsList, ProjectDetails, AddUserDialog, AddPartnerDialog } from "../../components";
import { BackOfficeService } from "../../../../api";
import styles from "./DSIProjects.module.css";
import { CustomDialog, createNotification } from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { downloadFile, isNonEmptyArray, searchInTree } from "../../../../common/utils";
import { useApi, useEffectOnce } from "../../../../common/hooks";

export default function DSIProjects({
	companies,
	companyRolesInProject,
	onAddExistingPartnerToProject,
	onAddExistingUserToProject,
	onUpdateCompanyRoleInProject,
	onUpdateUserStatusInProject,
	userRolesInProject,
}) {
	const { call: resetElasticSearchData } = useApi(BackOfficeService.resetElasticSearchData);
	const { call: updateProjectReqDetection } = useApi(BackOfficeService.updateProjectReqDetection);
	const { call: downloadProject } = useApi(BackOfficeService.downloadProject);
	const { call: restartProjectFailedDocuments } = useApi(BackOfficeService.restartProjectFailedDocuments);
	const { call: getAllProjectsDetails } = useApi(BackOfficeService.getAllProjectsDetails);
	const { call: updateProjectStatus } = useApi(BackOfficeService.updateProjectDetails);
	const [downloading, setDownloading] = useState(false);
	const [projectAction, setProjectAction] = useState(null);
	const [projectsRequest, setProjectsRequest] = useState(null);
	const [projectReqDetection, setProjectReqDetection] = useState(null);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [selectedProject, setSelectedProject] = useState(null);
	const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
	const [openAddPartnerDialog, setOpenAddPartnerDialog] = useState(false);
	const [openRelaunchDialog, setOpenRelaunchDialog] = useState(false);
	const [openReqDetectDialog, setOpenReqDetectDialog] = useState(false);
	const [toUpdate, setToUpdate] = useState(false);
	const isValidCompany = useCallback(
		(company) => company.type === "COMPANY" || company.type === "BUSINESS_UNIT" || company.type === "CONSORTIUM",
		[]
	);
	useEffect(() => {
		if (selectedCompany) {
			setSelectedCompany(
				searchInTree({ tree: companies, idToFind: selectedCompany?.id, additionalConditions: isValidCompany })
			);
		}
	}, [companies, selectedCompany, isValidCompany]);
	useEffect(() => {
		if (selectedCompany?.id) {
			setProjectsRequest(
				() =>
					({ page, limit }) =>
						getAllProjectsDetails({ companyId: selectedCompany.id }, { page, limit })
			);
		}
	}, [selectedCompany, getAllProjectsDetails, toUpdate]);

	const handleSelectCompany = (company) => {
		if (company !== selectedCompany) {
			setSelectedCompany(company);
			setSelectedProject(null);
		}
	};

	useEffectOnce(
		() => {
			handleSelectCompany(companies[0]);
		},
		[],
		() => selectedCompany === null && isNonEmptyArray(companies)
	);

	const handleSelectProject = (project) => {
		if (project !== selectedProject) {
			setSelectedProject({ ...project });
			setProjectReqDetection(project.reqDetection);
		}
	};
	const handleChangeCompanyRole = ({ role, company }) => {
		if (!role || !company || !selectedProject) {
			return;
		}
		onUpdateCompanyRoleInProject({ company, project: selectedProject, role });
	};
	const handleOnCloseReqDetectionDialog = () => setOpenReqDetectDialog(false);
	const handleReqDetection = () => {
		setProjectReqDetection(!projectReqDetection);
		updateProjectReqDetection({ projectId: projectAction.id });
		handleOnCloseReqDetectionDialog();
	};
	const onReqDetection = (project) => {
		setProjectAction(project);
		setOpenReqDetectDialog(true);
	};
	const handleAddUser = (user) => {
		if (!projectAction || !user) {
			return;
		}
		onAddExistingUserToProject({ company: selectedCompany, user, project: projectAction });
	};
	const handleUpdateUserStatusInProject = ({ selectedUser, selectedStatus }) => {
		onUpdateUserStatusInProject({
			company: selectedCompany,
			selectedUser,
			project: selectedProject,
			selectedStatus,
		});
	};
	const onAddUser = (project) => {
		setProjectAction(project);
		setOpenAddUserDialog(true);
	};
	const handleAddPartner = (partner) => {
		if (!projectAction || !partner) {
			return;
		}
		onAddExistingPartnerToProject({ selectedCompany, project: projectAction, company: partner });
	};
	const onAddPartner = (project) => {
		setProjectAction(project);
		setOpenAddPartnerDialog(true);
	};
	const handleDownload = (project) => {
		setDownloading(true);
		downloadProject({ projectId: project.id })
			.then(({ data, filename }) => downloadFile({ data, filename, filetype: "zip" }))
			.catch((err) => console.error(err))
			.finally(() => setDownloading(false));
	};
	const handleOnCloseRelaunchDialog = () => setOpenRelaunchDialog(false);
	const handleRelaunchDocs = () => {
		if (!projectAction) {
			return;
		}
		restartProjectFailedDocuments({ projectId: projectAction.id }).then((data) => {
			handleOnCloseRelaunchDialog();
			createNotification({
				message: translate("dsi.platform-management.restart-document-parsing.success", {
					docs: data.value,
				}),
			});
		});
	};
	const onRelaunchDocs = (project) => {
		setProjectAction(project);
		setOpenRelaunchDialog(true);
	};
	const handleOnCloseAddUserDialog = () => setOpenAddUserDialog(false);
	const handleOnCloseAddPartnerDialog = () => setOpenAddPartnerDialog(false);
	const handleChangeDetails = () => {
		setToUpdate((prev) => !prev);
	};
	const handleChangeArchive = ({ id, name, currentStatus, type, role, language }) => {
		updateProjectStatus(
			{ projectId: id },
			{ name, currentStatus: currentStatus === "ACTIVE" ? "ARCHIVED" : "ACTIVE", type, role, language }
		)
			.then(() => {
				createNotification({ type: "success", message: translate("dsi.project-details.name-update.success") });
				handleChangeDetails();
			})
			.catch((err) => console.error(err));
	};
	const handleResetElastic = (projectId) => {
		createNotification({
			message: translate("dsi.platform-management.restart-elastic-search.pending"),
		});
		resetElasticSearchData({ projectId })
			.then(() => {
				createNotification({
					type: "success",
					message: translate("dsi.platform-management.restart-elastic-search.success"),
				});
			})
			.catch(console.error);
	};
	return (
		<>
			<div className={styles["left-panel"]}>
				<CompaniesList
					companies={companies || []}
					selectedCompany={selectedCompany}
					onClickRow={handleSelectCompany}
				/>
			</div>
			<div className={styles["middle-panel"]}>
				<ProjectsList
					projectsRequest={projectsRequest}
					onAddPartner={onAddPartner}
					onAddUser={onAddUser}
					onChangeArchive={handleChangeArchive}
					onClickRow={handleSelectProject}
					onDownload={handleDownload}
					onRelaunchDocs={onRelaunchDocs}
					onResetElastic={handleResetElastic}
				/>
			</div>
			<ProjectDetails
				companyId={selectedCompany?.id}
				project={selectedProject}
				projectReqDetection={projectReqDetection}
				roles={companyRolesInProject}
				onChangeCompanyRole={handleChangeCompanyRole}
				onChangeDetails={handleChangeDetails}
				onChangeReqDetection={onReqDetection}
				onClose={() => {
					setSelectedProject(null);
				}}
				onUpdateStatus={handleUpdateUserStatusInProject}
			/>
			<AddUserDialog
				companyId={selectedCompany?.id}
				open={openAddUserDialog}
				roles={userRolesInProject}
				onClose={handleOnCloseAddUserDialog}
				onSubmit={handleAddUser}
			/>
			<AddPartnerDialog
				companies={companies || []}
				open={openAddPartnerDialog}
				selectedCompany={selectedCompany}
				onClose={handleOnCloseAddPartnerDialog}
				onSubmit={handleAddPartner}
			/>
			<CustomDialog
				loader
				maxWidth="xs"
				open={downloading}
				subTitle={translate("dsi.project-list.action.download-project-docs-loading")}
				title={translate("dsi.project-list.action.download-project-docs-process")}
			/>
			<CustomDialog
				open={openRelaunchDialog}
				subTitle={translate("dsi.project-list.action.restart-project-document-parsing.confirm", {
					project: projectAction?.name,
				})}
				title={translate("dsi.project-list.action.restart-project-document-parsing")}
				onClose={handleOnCloseRelaunchDialog}
				onSubmit={handleRelaunchDocs}
			/>
			<CustomDialog
				open={openReqDetectDialog}
				subTitle={translate("dsi.project-details.req-detection-dialog-confirm")}
				title={translate("dsi.project-details.req-detection-dialog")}
				onClose={handleOnCloseReqDetectionDialog}
				onSubmit={handleReqDetection}
			/>
		</>
	);
}
